import { ButtonBase, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { HLS_TRACK_TYPES, TRACK_TYPE_TO_MIME_TYPE } from 'src/consumer/constants'
import { TrackUploadButton } from '../helperComponentsStyles'
import { CustomSelect, selectTypes } from '../helperComponents'
import {
  checkIfStringIsValid,
  getVideoFileMetaData,
  giveMediaTypeBasedOnMimeType,
} from 'src/consumer/helpers/utilFunctions'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { TrackResponse } from 'src/common/tracks/TrackClass'
import { postDataWithoutParams } from 'src/consumer/services/featureServices'
import { getUppyUrl } from 'src/consumer/services/server_cdn_urls'
import AwsS3 from '@uppy/aws-s3'
import { Uppy } from '@uppy/core'
import '@uppy/core/dist/style.min.css'
import '@uppy/dashboard/dist/style.min.css'
import { Dashboard } from '@uppy/react'
import Webcam from '@uppy/webcam'
import '@uppy/webcam/dist/style.min.css'
import { TrackUploadDialogPaper } from 'src/mentor/components/ActivityPlanComponent/components/styledComponents'
import ReactPlayer from 'react-player'
import Upload from '@mui/icons-material/Upload'
import CopyAll from '@mui/icons-material/CopyAll'
import { getMPData } from 'src/mentor/services'

export const HLS_TRACK_TYPES_MAP = {
  hls_audio: 'hls_audio',
  hls_video: 'hls_video',
}

export default function TrackUploadComponent({
  propUppy,
  trackObject,
  handleChange = () => {},
  disabled,
}) {
  const [showModal, setShowModal] = useState(false)
  const [handlingUploads, setHandlingUploads] = useState(false)
  const currentTrackType = HLS_TRACK_TYPES?.find((trkTyp) => trkTyp.id === trackObject?.type)
  const [uppyServerUrl, setUppyServerUrl] = useState(null)
  const [uppy] = useState(
    () =>
      propUppy ||
      new Uppy({
        debug: true,
        autoProceed: true,
        restrictions: {
          allowedFileTypes: [
            TRACK_TYPE_TO_MIME_TYPE?.[HLS_TRACK_TYPES_MAP.hls_audio],
            TRACK_TYPE_TO_MIME_TYPE?.[HLS_TRACK_TYPES_MAP.hls_video],
          ],
          maxNumberOfFiles: 1,
        },
      }).use(Webcam),
  )

  const [showTrackOnDialog, setShowTrackOnDialog] = useState(false)

  async function handleResults(result) {
    if (result.failed.length === 0) {
      const successfulFile = result.successful?.[0]
      const response = successfulFile?.response
      const s3MultiPart = successfulFile?.s3Multipart
      const uploadedRawFile = successfulFile?.data
      let videoDuration = 0
      let videoWidth = 0
      let videoHeight = 0
      let contentType = successfulFile.type
      let objectKey = s3MultiPart?.key
      let fileSize = successfulFile.size
      let accessLevel = 'private'
      let type = giveMediaTypeBasedOnMimeType(successfulFile.type)
      let fileName = successfulFile.name

      const setVideoDuration = (duration) => {
        videoDuration = duration
      }
      const setVideoWidth = (width) => {
        videoWidth = width
      }
      const setVideoHeight = (height) => {
        videoHeight = height
      }

      await getVideoFileMetaData(uploadedRawFile)
        .then(({ duration, width, height }) => {
          // handleChange({ target: { id: 'duration', value: duration } })
          setVideoDuration(duration)
          setVideoWidth(width)
          setVideoHeight(height)
        })
        .catch((error) => {
          console.error(error)
        })
      console.log({
        response,
        s3MultiPart,
        uploadedRawFile,
        videoDuration,
        videoWidth,
        videoHeight,
        successfulFile,
      })
      return {
        accessLevel,
        contentType,
        duration: videoDuration,
        fileSize,
        height: videoHeight,
        objectKey,
        type,
        width: videoWidth,
        fileName,
      }
    }
    return null
  }

  function toggleShowModal() {
    uppy.cancelAll()
    setShowModal((prev) => !prev)
  }

  function changeTrackType(e) {
    handleChange((prev) => ({ ...TrackResponse, type: e.target.value }))
    uppy.setOptions({
      restrictions: { allowedFileTypes: [TRACK_TYPE_TO_MIME_TYPE?.[e.target.value] || ''] },
    })
  }

  uppy?.off('complete', null)?.once('complete', async (result) => {
    const metaDataToUpload = await handleResults(result, trackObject)

    if (!!metaDataToUpload && Object.keys(metaDataToUpload)?.length > 0 && !handlingUploads) {
      setHandlingUploads(true)
      const resp = await postDataWithoutParams(
        '/api/media/file/upload/s3/complete',
        metaDataToUpload,
      )

      if (resp) {
        const respData = resp.data
        handleChange((prev) => ({
          ...prev,
          duration: respData?.duration,
          fileId: respData?.fileId,
          url: `/${respData?.transcoding?.hls?.url}`,
        }))
      }
      await sleep(5000)
      setHandlingUploads(false)
    }
  })

  useEffect(() => {
    const fetchUppyConfig = async () => {
      try {
        const config = await getMPData('/api/secure/content/upload/configuration', {})
        if (config?.uppyServerUrl) {
          setUppyServerUrl(config.uppyServerUrl)
          uppy.use(AwsS3, {
            shouldUseMultipart: true,
            endpoint: `${config.uppyServerUrl}/companion`,
          })
        }
      } catch (error) {
        console.error('Failed to fetch uppy configuration:', error)
      }
    }

    if (!propUppy) {
      fetchUppyConfig()
    }
  }, [])

  useEffect(() => {
    !checkIfStringIsValid(trackObject?.type) &&
      handleChange({ target: { id: 'type', value: HLS_TRACK_TYPES?.[1]?.id } })
    uppy.setOptions({
      restrictions: { allowedFileTypes: [TRACK_TYPE_TO_MIME_TYPE?.[trackObject?.type] || ''] },
    })
  }, [trackObject?.type, disabled])

  return (
    <div className={'d-flex flex-column align-items-center gap-2 w-100'}>
      <div className={'d-flex gap-2 w-100 align-items-end'}>
        {disabled && (
          <div className="d-flex flex-column align-items-start gap-2">
            {currentTrackType ? (
              <ButtonBase
                disableTouchRipple
                sx={{ color: 'blue', textDecoration: 'underline' }}
                onClick={() => setShowTrackOnDialog(true)}
              >
                Track Type: {currentTrackType?.title}
              </ButtonBase>
            ) : (
              <div>Track Type: NA</div>
            )}
          </div>
        )}
        {!disabled && (
          <CustomSelect
            id="type"
            menuPortalTarget={document.body}
            value={trackObject?.type}
            isDisabled={disabled}
            label={'Track Type'}
            selectType={selectTypes.string}
            getOptionLabel={(option) => option?.title}
            getOptionValue={(option) => option?.id}
            options={HLS_TRACK_TYPES}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
            onChangeEvent={changeTrackType}
          />
        )}
        {!disabled && !!trackObject?.type && (
          <TrackUploadButton onClick={toggleShowModal} variant={'contained'}>
            <span>{trackObject?.url ? 'Change' : 'Upload'}</span> <Upload />
          </TrackUploadButton>
        )}
      </div>
      <Dialog open={showModal} onClose={toggleShowModal} PaperComponent={TrackUploadDialogPaper}>
        <DialogTitle>Upload {currentTrackType?.title}</DialogTitle>
        <DialogContent>
          <Dashboard
            uppy={uppy}
            plugins={['Webcam']}
            height={500}
            doneButtonHandler={toggleShowModal}
          />
        </DialogContent>
      </Dialog>
      {trackObject?.url && !disabled && (
        <div className={'d-flex flex-column gap-2 w-100 align-items-center'}>
          <h6 className={'align-self-start'}>
            Uploaded {currentTrackType?.title}{' '}
            <Tooltip title={`${trackObject?.url}`}>
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(`${currentTrackType?.title} URL`, trackObject.url)
                }}
              >
                <CopyAll />
              </IconButton>
            </Tooltip>
          </h6>
          <ReactPlayer
            url={`${trackObject?.url}`}
            style={{
              padding: '10px',
              maxWidth: '400px',
              maxHeight: '200px',
            }}
            height={trackObject?.type === HLS_TRACK_TYPES?.[0].id ? '70px' : '200px'}
            width={'100%'}
            controls={true}
          />
        </div>
      )}
      <Dialog open={showTrackOnDialog} onClose={() => setShowTrackOnDialog(false)}>
        <DialogContent>
          <ReactPlayer
            url={`${trackObject?.url}`}
            style={{
              padding: '10px',
              maxWidth: '400px',
              maxHeight: '200px',
            }}
            height={trackObject?.type === HLS_TRACK_TYPES?.[0].id ? '70px' : '200px'}
            width={'100%'}
            controls={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
